"use strict";

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { AG_GRID_LOCALE_VN } from "@ag-grid-community/locale";
import {
  Box,
  Button,
  Flex,
  Grid,
  Group,
  Input,
  Menu,
  MultiSelect,
  rem,
  ScrollArea,
  Text,
  TextInput,
} from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { useForm } from "@mantine/form";
import { useDebouncedValue, useDisclosure } from "@mantine/hooks";
import {
  IconBarcode,
  IconBuildingSkyscraper,
  IconBuildingWarehouse,
  IconCheck,
  IconFileExport,
  IconRestore,
  IconSearch,
  IconShoppingCartShare,
  IconTimeDuration15,
  IconX,
} from "@tabler/icons-react";
import {
  ColDef,
  GridReadyEvent,
  IDatasource,
  IGetRowsParams,
  InfiniteRowModelModule,
  ModuleRegistry,
} from "ag-grid-community";
import { AgGridReact, CustomCellRendererProps } from "ag-grid-react";
import dayjs from "dayjs";
import {
  paginationPageSizeSelector,
  repositoryDelivery,
  repositoryMdm,
  repositoryPos,
} from "../../../_base/_const/_constVar";
import BreadcrumbLayout1 from "../../../_base/component/_layout/_breadcrumb";
import { NotificationExtension } from "../../../_base/extension/NotificationExtension";
import LoadingOverlayCustom from "../../../common/LoadingOverLayCustom/LoadingOverLayCustom";
import { BASE_API_MDM } from "../../../config";
import { MessageResponse } from "../../../model/MessageResponse";

// Đăng ký module Infinite Row Model
ModuleRegistry.registerModules([InfiniteRowModelModule]);

export interface CountExportImportSerialItem {
  maTTSerial?: string | null;
  maCN?: string | null;
  maKho: string;
  tenViTri?: string | null;
  maVT: string;
  tenVT?: string | null;
  dvt?: string | null;
  serial?: string | null;
  soLanBan?: number | null;
  soLanNL?: number | null;
  soCTNhap?: string | null;
  ngayNhap?: string | null;
  maNCC?: string | null;
  tenNCC?: string | null;
  soCTPos?: string | null;
}

const CountExportImportSerial = () => {
  // Không cần state rowData hay loading thủ công nữa, AG Grid tự quản lý
  const gridRef = useRef<AgGridReact<any>>(null);
  const headerRef = React.useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState(750);

  // Data cho các ô select
  const [dataBranchCode, setDataBranchCode] = useState<any[]>([]);
  const [dataInvCode, setDataInvCode] = useState<any[]>([]);
  const [dataItem, setDataItem] = useState<any[]>([]);
  const [dataReasonRequestTransfer, setDataReasonRequestTransfer] = useState<
    any[]
  >([]);

  // Search item logic
  const [searchItem, setSearchItem] = useState("");
  const [debouncedItemCode] = useDebouncedValue(searchItem, 500);
  const [loading, setLoading] = useState(false);

  const containerStyle = useMemo(
    () => ({ width: "100%", height: height }),
    [height],
  );
  const gridStyle = useMemo(() => ({ height: "100%", width: "100%" }), []);
  const sizeGrid = { base: 12, sm: 6, md: 6, lg: 2 };
  const [visible, { toggle, close, open }] = useDisclosure(false);

  // Form Filter
  const form = useForm<{
    keySearch: string;
    MaChiNhanh: string[];
    MaKho: string[];
    MaVatTu: string[];
    TuNgayNhap: string | null;
    DenNgayNhap: string | null;
    VatTuCode: string | null;
    Serial: string | null;
    SoLanBan: string | null;
    SoLanNL: string | null;
  }>({
    initialValues: {
      keySearch: "",
      MaChiNhanh: [],
      MaKho: [],
      MaVatTu: [],
      TuNgayNhap: dayjs().subtract(1, "month").format("YYYY-MM-DD"),
      DenNgayNhap: dayjs().format("YYYY-MM-DD"),
      VatTuCode: null,
      Serial: null,
      SoLanBan: null,
      SoLanNL: null,
    },
  });

  // Auto resize height
  useEffect(() => {
    const updateHeight = () => {
      const headerHeight = headerRef.current?.offsetHeight || 0;
      const newHeight = window.innerHeight - (210 + headerHeight);
      setHeight(newHeight);
    };
    setTimeout(updateHeight, 0);
    window.addEventListener("resize", updateHeight);
    return () => window.removeEventListener("resize", updateHeight);
  }, []);

  // --- LOGIC GỌI API (Được gọi bởi Datasource) ---
  const fetchData = async (startRow: number, endRow: number) => {
    try {
      // Tính toán Skip và Take dựa trên params của AG Grid
      const skip = startRow;
      const take = endRow - startRow; // Thường là 100

      let url = `/api/v1/SerialSalesRestockReport/get-list?isAll=true&Skip=${skip}&Take=${take}`;
      const values = form.getValues();

      // Append filters
      if (values.MaChiNhanh && values.MaChiNhanh.length > 0) {
        values.MaChiNhanh.forEach((branchCode: string) => {
          url += `&MaChiNhanh=${branchCode}`;
        });
      }
      if (values.MaKho && values.MaKho.length > 0) {
        values.MaKho.forEach((invCode: string) => {
          url += `&MaKho=${invCode}`;
        });
      }
      if (values.VatTuCode) {
        values.VatTuCode.split(";")
          .map((s) => s.trim())
          .filter(Boolean)
          .forEach((maVatTu: string) => {
            url += `&MaVatTu=${maVatTu}`;
          });
      }
      if (values.TuNgayNhap) url += `&TuNgayNhap=${values.TuNgayNhap}`;
      if (values.DenNgayNhap) url += `&DenNgayNhap=${values.DenNgayNhap}`;
      if (values.Serial) url += `&Serial=${values.Serial}`;
      if (values.keySearch) url += `&KeySearch=${values.keySearch.trim()}`;
      if (values.SoLanBan && Number(values.SoLanBan) >= 0) {
        url += `&SoLanBan=${values.SoLanBan}`;
      }
      if (values.SoLanNL && Number(values.SoLanNL) >= 0) {
        url += `&SoLanNL=${values.SoLanNL}`;
      }
      const response =
        await repositoryMdm.get<MessageResponse<CountExportImportSerialItem[]>>(
          url,
        );
      return response;
    } catch (error) {
      console.error("API Error:", error);
      return null;
    }
  };

  // --- TẠO DATASOURCE CHO INFINITE ROW MODEL ---
  const getDatasource = useCallback((): IDatasource => {
    return {
      getRows: async (params: IGetRowsParams) => {
        const { startRow, endRow } = params;
        gridRef.current?.api?.showLoadingOverlay();
        try {
          const response = await fetchData(startRow, endRow);

          if (response && response.success) {
            const data = response.data || [];
            const totalCount = (response as any).totalCount ?? 0;
            gridRef.current?.api?.hideOverlay();
            params.successCallback(data, totalCount);
          } else {
            params.failCallback();
            gridRef.current?.api?.hideOverlay();
          }
        } catch (e) {
          params.failCallback();
          gridRef.current?.api?.hideOverlay();
        }
      },
    };
  }, [form.getValues()]); // Re-create datasource khi form values thay đổi (thực ra ko cần thiết nếu dùng setDatasource ở handleSearch)

  // --- SỰ KIỆN BẤM TÌM KIẾM ---
  const handleSearch = useCallback(() => {
    form.setErrors({});
    const values = form.getValues();
    console.log(values);
    if (
      values.TuNgayNhap &&
      values.DenNgayNhap &&
      dayjs(values.TuNgayNhap).isAfter(dayjs(values.DenNgayNhap))
    ) {
      form.setFieldError(
        "TuNgayNhap",
        "Ngày bắt đầu phải nhỏ hơn hoặc bằng ngày kết thúc",
      );
      return;
    }

    // if (
    //   (!values.Serial || String(values.Serial).length === 0) &&
    //   !values.TuNgayNhap &&
    //   !values.DenNgayNhap
    // ) {
    //   NotificationExtension.Fails(
    //     "Vui lòng nhập Serial hoặc thời gian tìm kiếm trong khoảng 1 tháng !",
    //   );
    // } else if (
    //   (!values.Serial || String(values.Serial).length === 0) &&
    //   values.TuNgayNhap &&
    //   values.DenNgayNhap
    // ) {
    //   const fromDate = new Date(values.TuNgayNhap || "");
    //   const toDate = new Date(values.DenNgayNhap || "");

    //   const maxAllowedDate = new Date(fromDate);
    //   maxAllowedDate.setMonth(fromDate.getMonth() + 1);

    //   if (toDate > maxAllowedDate) {
    //     NotificationExtension.Fails(
    //       "Bạn chỉ được phép lọc dữ liệu trong khoảng 1 tháng nếu không nhập dữ liệu tìm kiếm Serial !",
    //     );
    //     return;
    //   }
    // }

    if (gridRef.current && gridRef.current.api) {
      // Set lại datasource mới sẽ kích hoạt Grid reset về trang 1 và gọi lại getRows với filter mới
      gridRef.current.api.setGridOption("datasource", getDatasource());
    }
  }, [form, getDatasource]);

  // --- ON GRID READY ---
  const onGridReady = useCallback(
    (params: GridReadyEvent) => {
      // Gán datasource ban đầu
      params.api.setGridOption("datasource", getDatasource());
    },
    [getDatasource],
  );

  // --- CÁC HÀM TIỆN ÍCH KHÁC GIỮ NGUYÊN ---
  const getRowId = useCallback((params: any) => {
    const d = params.data || {};
    return (
      `${d.maVT}_${d.serial}_${d.soCTNhap}`.replace(/(^_+|_+$)/g, "") ||
      Math.random().toString()
    );
  }, []);

  const getRowStyle = useCallback((params: any) => {
    if (params.node.rowPinned) {
      return {
        backgroundColor: "#e7f5ff",
        fontWeight: 700,
        fontSize: "14px",
        borderTop: "3px solid #15aabf",
      };
    }
    return undefined;
  }, []);

  const columnDefs = useMemo<ColDef[]>(
    () => [
      // {
      //   field: "maTTSerial",
      //   headerName: "Mã TT Serial",
      //   width: 150,
      // },
      {
        field: "soCTNhap",
        headerName: "Số chứng từ nhập",
        minWidth: 160,
        valueFormatter: (p: any) => p.value ?? "",
      },
      {
        field: "soCTPos",
        headerName: "Số chứng từ POS",
        minWidth: 160,
        valueFormatter: (p: any) => p.value ?? "",
      },
      {
        field: "soLanBan",
        headerName: "Số lần bán",
        width: 150,
        valueFormatter: (p: any) => (p.value != null ? String(p.value) : ""),
      },
      {
        field: "soLanNL",
        headerName: "Số lần nhập lại",
        width: 150,
        valueFormatter: (p: any) => (p.value != null ? String(p.value) : ""),
      },
      { field: "tenViTri", headerName: "Tên vị trí", minWidth: 140 },
      { field: "maCN", headerName: "Mã chi nhánh", width: 120 },
      { field: "maKho", headerName: "Mã kho", width: 140 },
      {
        field: "maNCC",
        headerName: "Mã nhà cung cấp",
        width: 160,
        valueFormatter: (p: any) => p.value ?? "",
      },
      {
        field: "tenNCC",
        headerName: "Tên nhà cung cấp",
        minWidth: 180,
        valueFormatter: (p: any) => p.value ?? "",
      },
      { field: "serial", headerName: "Serial", width: 160 },
      { field: "maVT", headerName: "Mã vật tư", width: 140 },
      { field: "tenVT", headerName: "Tên vật tư", minWidth: 220 },
      { field: "dvt", headerName: "Đơn vị tính", width: 120 },
      {
        field: "ngayNhap",
        headerName: "Ngày nhập",
        minWidth: 160,
        valueFormatter: (p: any) => {
          const v = p.value;
          if (!v) return "";
          const d = dayjs(v);
          return d.isValid() ? d.format("DD-MM-YYYY HH:mm:ss") : String(v);
        },
      },
      {
        field: "lyDoDoiTra",
        headerName: "Lý do đổi trả",
      },
      {
        field: "lyDoDieuChuyen",
        headerName: "Lý do điều chuyển",
        cellRenderer: (params: CustomCellRendererProps) => {
          return (
            <Text mt={10}>
              {dataReasonRequestTransfer.find((i) => i.code === params.value)
                ?.name || params.value}
            </Text>
          );
        },
      },
    ],
    [dataReasonRequestTransfer],
  );

  const defaultColDef = useMemo<ColDef>(() => {
    return {
      sortable: false, // Infinite model server-side sort hơi phức tạp, tạm tắt hoặc cần handle trong getRows
      resizable: true,
    };
  }, []);

  // --- FETCH DATA FOR SELECTS (Giữ nguyên) ---
  const DataInvCode = async () => {
    /* ... Logic cũ ... */
    const branchSearch =
      dataBranchCode.length > 0 && form.getValues()?.MaChiNhanh?.length > 0
        ? form
            .getValues()
            ?.MaChiNhanh.map((branchCode: string) => {
              const branch = dataBranchCode.find((b) => b.value === branchCode);
              return branch ? `&BranchId=${branch.id}` : "";
            })
            .join("")
        : "";

    const take = form.getValues()?.MaChiNhanh?.length * 100;
    const res = await repositoryDelivery.get<MessageResponse<any>>(
      `/api/v1/TblDmInventory/get-list-by-branchs?Skip=0&Take=${
        form.getValues()?.MaChiNhanh && form.getValues()?.MaChiNhanh?.length > 0
          ? take
          : 100
      }${branchSearch}`,
    );

    if (res && res.success) {
      const result = res.data;
      const data = result
        ?.filter((item: any) => item.idBranch && item.name)
        ?.map((item: any) => ({
          label: `${item.code} - ${item.name}`,
          value: item.code?.toString(),
        }));
      setDataInvCode(data);
    }
  };

  const DataBrachCode = async () => {
    /* ... Logic cũ ... */
    try {
      const response = await repositoryPos.get(
        `api/v1/TblDmInventory/get-select-branch?auth=true`,
      );
      if (response && response.success) {
        const transformedData = response.data
          .filter((i: any) => i.att2 && i.text)
          .map((item: any) => {
            return {
              value: item.att2,
              label: item.att2 + " - " + item.text,
              id: item.value,
            };
          });
        setDataBranchCode(transformedData);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const fetchDataReasonRequestTransfer = async () => {
    const res = await repositoryPos.get<MessageResponse<any>>(
      "/api/v1/TblTransferReason/get-list?Skip=0&Take=300",
    );

    if (res && res.success) {
      const data = res.data;
      setDataReasonRequestTransfer(data);
    }
  };

  const handleCleanKeySearch = (str: string) => {
    if (typeof str !== "string") return "";
    return str.replace(/;+/g, ";").replace(/;\s*$/, "");
  };

  const getDataItem = async () => {
    /* ... Logic cũ ... */
    try {
      let url = "";
      if (
        handleCleanKeySearch(searchItem ?? "").length > 1 &&
        handleCleanKeySearch(searchItem ?? "")
          .trim()
          .includes(";")
      ) {
        const cleanKeySearch = handleCleanKeySearch(searchItem ?? "")
          ?.toUpperCase()
          ?.trim();
        url += `${cleanKeySearch
          ?.split(";")
          ?.map((item) => `&ListItemCode=${item.trim().replace(" ", "")}`)
          .join("")}`;
      } else if (
        handleCleanKeySearch(searchItem ?? "")?.includes(";") === false &&
        (searchItem ?? "")?.length > 0
      ) {
        url += `&ListItemCode=${searchItem?.toUpperCase()}`;
      }
      const response = await repositoryPos.get(
        `api/v1/TblItem/get-list?Skip=0&Take=100` + url,
      );
      if (response && response.success) {
        const transformedData = response.data
          ?.filter((item: any) => item.itemCode && item.itemName)
          .map((item: any) => ({
            label: item.itemCode + " - " + item.itemName,
            value: item.itemId?.toString(),
            code: item.itemCode,
          }));
        const uniqueData = Array.from(
          new Set(transformedData.map((item: any) => item.value)),
        ).map((value) =>
          transformedData.find((item: any) => item.value === value),
        );
        setDataItem(uniqueData);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const menuItems = useMemo(() => {
    /* ... Logic cũ ... */
    if (dataItem.length === 0)
      return (
        <Menu.Item>
          <Flex justify="center">
            <Text size="sm">
              {searchItem.length > 0
                ? "Không tìm thấy dữ liệu !!!"
                : "Nhập mã vật tư để tìm kiếm !!!"}
            </Text>
          </Flex>
        </Menu.Item>
      );
    return dataItem.map((item, index) => (
      <Menu.Item
        key={`${item.value}-${index}`}
        onClick={() => {
          const updatedItemCode = searchItem
            ?.toUpperCase()
            .includes(item.code?.toUpperCase())
            ? searchItem
                .toUpperCase()
                .split(";")
                .filter((val) => val.trim() !== item.code.toUpperCase())
                .join(";")
            : searchItem === ""
              ? item.code
              : `${searchItem};${item.code}`;
          form.setFieldValue("VatTuCode", updatedItemCode);
          setSearchItem(updatedItemCode);
        }}
      >
        <Flex align="center" gap="xs">
          {searchItem.toUpperCase().includes(item.code?.toUpperCase()) && (
            <IconCheck size={14} color="#808080" />
          )}
          <Text size="sm" truncate="end">
            {item.label}
          </Text>
        </Flex>
      </Menu.Item>
    ));
  }, [dataItem, searchItem]);

  useEffect(() => {
    if (debouncedItemCode.length > 0) {
      getDataItem();
    } else {
      setDataItem([]);
    }
  }, [debouncedItemCode]);

  useEffect(() => {
    DataBrachCode();
    DataInvCode();
    fetchDataReasonRequestTransfer();
  }, []);

  // ExportData Excel
  const exportDataExcel = async () => {
    open();
    try {
      let params = new URLSearchParams();
      const values = form.getValues();

      if (values.keySearch) {
        params.append("KeySearch", values.keySearch.trim());
      }
      if (values.Serial) {
        params.append("Serial", values.Serial.trim());
      }
      if (values.MaChiNhanh && values.MaChiNhanh.length > 0) {
        values.MaChiNhanh.forEach((branchCode: string) => {
          params.append("MaChiNhanh", branchCode);
        });
      }
      if (values.MaKho && values.MaKho.length > 0) {
        values.MaKho.forEach((invCode: string) => {
          params.append("MaKho", invCode);
        });
      }
      if (values.VatTuCode) {
        values.VatTuCode.split(";")
          .map((s) => s.trim())
          .filter(Boolean)
          .forEach((maVatTu: string) => {
            params.append("MaVatTu", maVatTu);
          });
      }
      if (values.TuNgayNhap) {
        params.append("TuNgayNhap", values.TuNgayNhap);
      }
      if (values.DenNgayNhap) {
        params.append("DenNgayNhap", values.DenNgayNhap);
      }
      if (values.SoLanBan && Number(values.SoLanBan) >= 0) {
        params.append("SoLanBan", values.SoLanBan);
      }
      if (values.SoLanNL && Number(values.SoLanNL) >= 0) {
        params.append("SoLanNL", values.SoLanNL);
      }

      const url = `${BASE_API_MDM}/api/v1/SerialSalesRestockReport/export?isAll=true${
        params.toString() ? `&${params.toString()}` : ""
      }`;
      const token = localStorage.getItem("token")?.replace(/"/g, "");

      const response = await fetch(url, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/vnd.ms-excel",
        },
      });

      if (!response.ok) throw new Error("Failed to dowload file");

      const blob = await response.blob();
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      link.download =
        "bao-cao-dem-so-lan-xuat-ban-va-nhap-lai-cho-tung-serial.xlsx";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(link.href);
    } catch (error) {
      console.error("Error:", error);
      NotificationExtension.Fails("Xuất Excel thất bại");
    } finally {
      close();
    }
  };

  return (
    <Box pos={"relative"} style={containerStyle}>
      <LoadingOverlayCustom visible={visible} />

      <div
        style={{
          border: "1px solid #dee2e6",
          padding: "10px",
          overflowX: "hidden",
        }}
      >
        <Group justify="space-between" gap={0}>
          <BreadcrumbLayout1 />
          <Button
            leftSection={
              <IconFileExport style={{ width: rem(14), height: rem(14) }} />
            }
            onClick={exportDataExcel}
          >
            Export Excel
          </Button>
        </Group>
        <Grid mt={10}>
          {/* GIỮ NGUYÊN UI FILTER NHƯ CŨ */}
          <Grid.Col span={sizeGrid}>
            <Input
              placeholder="Nhập tìm kiếm"
              leftSection={<IconSearch size={20} color="#15aabf" />}
              value={form.getValues().keySearch}
              onChange={(e) => form.setFieldValue("keySearch", e.target.value)}
              onKeyDown={async (e) => {
                if (e.key === "Enter") handleSearch();
              }}
            />
          </Grid.Col>
          <Grid.Col span={sizeGrid}>
            <MultiSelect
              placeholder="Chi nhánh"
              clearable
              searchable
              data={dataBranchCode}
              value={form.getValues().MaChiNhanh}
              onChange={(v) => {
                form.setFieldValue("MaChiNhanh", v);
                DataInvCode();
              }}
              leftSection={<IconBuildingSkyscraper size={20} color="#15aabf" />}
            />
          </Grid.Col>
          <Grid.Col span={sizeGrid}>
            <MultiSelect
              size="sm"
              placeholder="Mã kho"
              searchable
              clearable
              data={dataInvCode}
              value={form.getValues().MaKho}
              onChange={(v) => form.setFieldValue("MaKho", v)}
              leftSection={<IconBuildingWarehouse size={20} color="#15aabf" />}
            />
          </Grid.Col>
          <Grid.Col span={sizeGrid}>
            <Box w={"100%"} style={{ position: "relative" }}>
              <Menu
                width="target"
                position="bottom"
                offset={5}
                keepMounted
                closeOnItemClick={false}
              >
                <Menu.Target>
                  <TextInput
                    placeholder="Nhập mã vật tư"
                    leftSection={<IconBarcode size={20} color="#15aabf" />}
                    rightSectionPointerEvents="all"
                    rightSection={
                      <IconX
                        size={20}
                        color="#15aabf"
                        onClick={() => {
                          setSearchItem("");
                          form.setFieldValue("VatTuCode", "");
                        }}
                        style={{
                          display: searchItem.length > 0 ? undefined : "none",
                          cursor: "pointer",
                        }}
                      />
                    }
                    value={searchItem}
                    onChange={(e) => {
                      setSearchItem(e.currentTarget.value);
                      form.setFieldValue("VatTuCode", e.currentTarget.value);
                    }}
                  />
                </Menu.Target>
                <Menu.Dropdown>
                  <ScrollArea
                    h={dataItem.length <= 1 ? 32 : 225}
                    offsetScrollbars
                    scrollbars="y"
                  >
                    {menuItems}
                  </ScrollArea>
                </Menu.Dropdown>
              </Menu>
            </Box>
          </Grid.Col>
          <Grid.Col span={sizeGrid}>
            <TextInput
              placeholder="Serial"
              leftSection={<IconBarcode size={20} color="#15aabf" />}
              value={form.getValues().Serial || ""}
              {...form.getInputProps("Serial")}
              onChange={(e) => form.setFieldValue("Serial", e.target.value)}
              onKeyDown={async (e) => {
                if (e.key === "Enter") handleSearch();
              }}
            />
          </Grid.Col>
          <Grid.Col span={sizeGrid}>
            <TextInput
              placeholder="Số lần xuất bán"
              leftSection={<IconShoppingCartShare size={20} color="#15aabf" />}
              type="number"
              value={form.getValues().SoLanBan || ""}
              {...form.getInputProps("SoLanBan")}
              onChange={(e) =>
                form.setFieldValue("SoLanBan", e.currentTarget.value)
              }
              onKeyDown={async (e) => {
                if (e.key === "Enter") handleSearch();
              }}
            />
          </Grid.Col>
          <Grid.Col span={sizeGrid}>
            <TextInput
              placeholder="Số lần nhập lại"
              leftSection={<IconRestore size={20} color="#15aabf" />}
              type="number"
              value={form.getValues().SoLanNL || ""}
              {...form.getInputProps("SoLanNL")}
              onChange={(e) =>
                form.setFieldValue("SoLanNL", e.currentTarget.value)
              }
              onKeyDown={async (e) => {
                if (e.key === "Enter") handleSearch();
              }}
            />
          </Grid.Col>
          <Grid.Col span={sizeGrid}>
            <DatePickerInput
              size="sm"
              placeholder="Chọn ngày bắt đầu"
              locale="vi"
              valueFormat="DD/MM/YYYY"
              leftSection={<IconTimeDuration15 size={20} color="#15aabf" />}
              {...form.getInputProps("TuNgayNhap")}
              value={
                form.getValues().TuNgayNhap
                  ? dayjs(form.getValues().TuNgayNhap, "YYYY-MM-DD").toDate()
                  : null
              }
              onChange={(date) =>
                form.setFieldValue(
                  "TuNgayNhap",
                  date ? dayjs(date).format("YYYY-MM-DD") : null,
                )
              }
              clearable
            />
          </Grid.Col>
          <Grid.Col span={sizeGrid}>
            <DatePickerInput
              size="sm"
              placeholder="Chọn ngày kết thúc"
              locale="vi"
              valueFormat="DD/MM/YYYY"
              leftSection={<IconTimeDuration15 size={20} color="#15aabf" />}
              {...form.getInputProps("DenNgayNhap")}
              value={
                form.getValues().DenNgayNhap
                  ? dayjs(form.getValues().DenNgayNhap, "YYYY-MM-DD").toDate()
                  : null
              }
              onChange={(date) =>
                form.setFieldValue(
                  "DenNgayNhap",
                  date ? dayjs(date).format("YYYY-MM-DD") : null,
                )
              }
              clearable
            />
          </Grid.Col>
          <Grid.Col span={{ base: 12, sm: 6, md: 6, lg: 0.9 }}>
            <Button color="blue" variant="outline" onClick={handleSearch}>
              Tìm kiếm
            </Button>
          </Grid.Col>
        </Grid>
      </div>

      <div style={gridStyle} className={"ag-theme-quartz"}>
        <AgGridReact<CountExportImportSerialItem>
          ref={gridRef}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          // --- CẤU HÌNH QUAN TRỌNG CHO INFINITE ROW MODEL ---
          rowModelType={"infinite"} // Chế độ load theo trang từ server
          // Kích thước mỗi lần gọi API (Take = 100)
          cacheBlockSize={100}
          // Số dòng tối đa giữ trong cache trình duyệt (tùy chỉnh để tối ưu RAM)
          maxBlocksInCache={10}
          // Hiển thị phân trang
          pagination={true}
          paginationPageSize={100} // Phải khớp hoặc là bội số của cacheBlockSize
          paginationPageSizeSelector={paginationPageSizeSelector} // [100, 200, 500]
          onGridReady={onGridReady}
          getRowId={getRowId}
          getRowStyle={getRowStyle}
          localeText={AG_GRID_LOCALE_VN}
        />
      </div>
    </Box>
  );
};
export default CountExportImportSerial;
